import React from 'react'
import Head from 'next/head'
import { WarningSignsSoundsSection } from '../CarPage/WarningSignsSoundsSection'
import { FAQSection } from '../CarPage/FAQSection'
import { formatPhoneNumber } from '../utils/string-utils'
import dynamic from 'next/dynamic'
import Layout from '../Layout'
import { FeatureBySection } from '../FeatureBySection'
import { HowItWorksSection } from '../LandingPage/HowItWorksSection'
import { ConvenientAffordableSection } from '../LandingPage/ConvenientAffordableSection'
import { ReviewProps, ReviewsSection } from '../SEMPage/ReviewsSection'
import { HappinessGuaranteeSection } from '../HappinessGuaranteeSection'
import { BrakeRepairNearYouWithCities } from '../LandingPage/BrakeRepairNearYouWithCities'
import { CarBrandGrid } from '../Builder/components/CarBrandGrid'
import { FastAndReliableSection } from './FastAndReliableSection'
import { ServicesSection } from '../ServicesSection'
import { CitiesCTA } from './CitiesCTA'
import { Container } from '../Container'
import { V2BookFactsSection } from '../CarPage/V2BookFactsSection'
import { CityPhoneNumberData } from '../SEMPage/CityPhoneNumberData'
import { CityData } from './index'

const CityNearYouSection = dynamic(() => import('./CityNearYouSection').then((mod) => mod.CityNearYouSection))

import site from '../../../public/img/get-quote.svg'
import calendar from '../../../public/img/calendar-icon.svg'
import brakes from '../../../public/img/brakes.svg'
import wrench from '../../../public/img/technicians@3x.png'
import brakeVan from '../../../public/img/brake-van.png'
import brakeParts from '../../../public/img/brake-rotor.png'
import smiles from '../../../public/img/customer-service.png'
import ribbon from '../../../public/img/warranty.png'
import openSign from '../../../public/img/open-sign.png'
import { HomeScheduleServiceFormHeaderInline } from '../Builder/components/HeroInline'

type CityPostTemplateProps = {
  cityName: string
  rating: string
  reviewCount: number
  stateAbbreviation: string
  backgroundImage: any
  listOfZips: number[]
  listOfMinorCities: string[]
  tags: string[]
  schemas: any[]
  titleTagTitle: string
  metaDescription: string
  reviews: ReviewProps[]
  blobSection: any
  blobParagraphs: string[]
  phoneNumber: string
  cities: CityData[]
  carsContent: any[]
}
export const MajorCityPageTemplate: React.FC<CityPostTemplateProps> = ({
  cityName,
  stateAbbreviation,
  cities,
  listOfZips,
  listOfMinorCities,
  schemas,
  metaDescription,
  titleTagTitle,
  reviews,
  phoneNumber,
  rating,
  reviewCount,
  blobSection,
  blobParagraphs,
  carsContent = [],
}) => {
  return (
    <Layout title={titleTagTitle} phoneNumber={`${phoneNumber}`} phoneNumberTxt={formatPhoneNumber(`${phoneNumber}`)!}>
      <Head>
        <meta
          name={'description'}
          content={
            metaDescription
              ? metaDescription
              : `Schedule mobile brake repair services in ${cityName}, ${stateAbbreviation}. Affordable repairs, no upsells, and insanely good customer service. We come to you!`
          }
        />
        {schemas &&
          schemas.map((schema, index) => {
            const blob = JSON.stringify(JSON.parse(schema.blob))
            return <script type="application/ld+json" key={index} dangerouslySetInnerHTML={{ __html: blob }} />
          })}
      </Head>
      <HomeScheduleServiceFormHeaderInline
        title={`The Best Mobile Brake Repair in ${cityName} — We Come To You.`}
        subheading={'Schedule your mobile brake repair today!'}
        headerTitle={'Book affordable repairs without leaving your home.'}
        subtitle={'Our repair experts will send you a quote in under 5 minutes.'}
        rating={rating}
        reviewCount={reviewCount}
        buttonText={'GET A FREE QUOTE'}
        isLinkForm={true}
        ratingsText={'customers'}
        linkTarget={'get-a-quote/services'}
      />

      <FeatureBySection />
      <HowItWorksSection
        title={`Skip The ${cityName} Traffic, We Come To You.`}
        description={`Let our ${cityName} mobile brake repair technicians take care of your brake problems.`}
        steps={[
          {
            title: 'Get Your Free Repair Quote',
            description:
              'Let us know what’s going on with your brakes and we’ll send you a free, no obligation repair quote for your vehicle.',
            image: site,
          },
          {
            title: 'Select Your Repair Date',
            description:
              'Select a repair date and time that works around your schedule. We pride ourselves on making it simple for you.',
            image: calendar,
          },
          {
            title: 'Enjoy Safer, Better Driving',
            description:
              'Our brake technicians will ensure that your brakes are ready to hit the road and get you back to driving worry-free!',
            image: brakes,
          },
        ]}
        buttonText={`Get A Free ${cityName} Brake Service Estimate`}
      />
      <ConvenientAffordableSection
        title={`Convenient & Affordable ${cityName} Brake Repair`}
        description={`Do you really want to waste time sitting at a brake shop? Our ${cityName} mobile brake technicians work on your vehicle right where it's parked at your work or home.`}
        reasons={[
          {
            title: `Save Time & Money`,
            description: `Save up to 40% on average when compared to taking your car to a local auto repair shop.`,
            icon: brakeVan,
          },
          {
            title: `Experienced Brake Technicians`,
            description: `All of our mobile brake technicians are ASE-certified and able to take care of all your brake repair or replacement needs.`,
            icon: wrench,
          },
          {
            title: `Premium Brake Parts`,
            description: `We offer premium brake parts with no markup, something that can’t be said by the big box shops.`,
            icon: brakeParts,
          },
          {
            title: `Unreal Customer Service`,
            description: `Have a question about your brakes before, during or after the service? We’re here to help.`,
            icon: smiles,
          },
          {
            title: `NuBrakes Guarantee`,
            description: `Our repairs are backed by our NuBrakes Guarantee to provide peace of mind. Read our warranty for more information.`,
            icon: ribbon,
          },
          {
            title: `Available When You Need Us`,
            description: `We want to make brake repairs convenient, so we operate well past the time most traditional shops do.`,
            icon: openSign,
          },
        ]}
      />
      <ReviewsSection
        phoneNumber={phoneNumber || CityPhoneNumberData.default.phoneNumber}
        rating={rating}
        reviewCount={reviewCount}
        title={"Don't Just Take Our Word For It."}
        description={'Drivers trust NuBrakes to handle their brake needs.'}
        reviews={reviews}
      />
      <V2BookFactsSection
        title={blobSection.title}
        subheading1={blobSection.subheading1}
        subheading2={blobSection.subheading2}
        paragraphs={blobParagraphs}
      />
      <WarningSignsSoundsSection
        title={`${cityName} Brake Problems & Symptoms`}
        subheading={`Warning Signs & Sounds`}
        formalMakeName={'Vehicle'}
      />
      <HappinessGuaranteeSection
        title={'We have a 100% NuBrakes Happiness Guarantee!'}
        description={
          "We want to make the brake repair process simple and frustration-free. If you're not happy with your repair or replacement, we'll do everything we can to fix it. Call or message us any day, any time, and we'll do our best to fix whatever issue you're having."
        }
      />

      <ServicesSection cityName={cityName} className="pt-12 pb-6 sm:pb-12" />

      <Container>
        <CitiesCTA phoneNumber={phoneNumber || CityPhoneNumberData.default.phoneNumber} />
      </Container>

      <div className="border-b-[1px] border-gray-100 pt-12" />

      <CarBrandGrid
        title={`We Offer Auto Repair Services On Nearly Every Model in ${cityName}`}
        carContent={carsContent}
        className="pt-16"
      >
        {<div className="mt-6" />}
      </CarBrandGrid>

      <FastAndReliableSection title={`FAST AND RELIABLE BRAKE REPAIR IN ${cityName}`} />

      <FAQSection
        title={`Frequently Asked ${cityName} Repair Questions`}
        description={`Learn more about our ${cityName} brake repair operations`}
        faqs={[
          {
            question: `How is NuBrakes different from other ${cityName} brake repairs near me?`,
            answer: `NuBrakes provides a modern approach to brake repair and replacements by providing an on-demand style service that comes to you. Unlike traditional ${cityName} brake service shops that require to you to wait in the lobby while the brake repair is being done, use poor quality parts, and lack transparent pricing; NuBrakes will start by sending you a transparent quote, upon your approval, a NuBrakes ASE-certified technician will come to you with everything they need to repair your brakes. We want to save you time and provide you with a trustworthy and transparent diagnosis and repair quote for your ${cityName}. To request a quote, simply click here, fill out your vehicle information and brake symptoms, and we’ll send you a quote for your ${cityName} brake service in under an hour.`,
          },
          {
            question: `Are your ${cityName} brake service technicians ASE-certified?`,
            answer: `Absolutely. Every NuBrakes ${cityName} brake repair technician has years of industry experience and has been certified by The National Institute for Automotive Service Excellence (ASE). Our technicians have a depth of knowledge in all forms of vehicle repair, but brake repair is our core service and specialty.`,
          },
          {
            question: `Why should I choose mobile brake repair?`,
            answer: `We’re a firm believer that your time, energy, and a trustworthy service provider is much more valuable than spending your Saturday afternoon at the brake repair shop. We’ve built the NuBrakes service so that you can efficiently receive a quote and schedule a mobile brake repair service that comes to you, with just a few clicks. There’s not enough time in the day and we think you should spend more time doing what you love and waiting at the brake repair shop is not it. Not to mention, many brake repair shops will quote you more than you need to spend, and often suggest you repair or replace brake parts when it is not necessary. What’s better than saving time and money?`,
          },
          {
            question: `What type of brake pads does NuBrakes use?`,
            answer: `High-quality parts are of the utmost importance when repairing your brakes. At NuBrakes, most frequently we are using professional ceramic or semi-metallic brake pads. These parts offer exceptional performance and are backed by our 24 Month/24,000 Mile warranty.`,
          },
          {
            question: `How long does it take to repair or replace my brakes?`,
            answer: `On average, your mobile brake repair service will take 45-90 minutes, with the average appointment lasting around an hour. If you have any time constraints, we’re happy to coordinate around your schedule.`,
          },
          {
            question: `What should I expect during my brake repair appointment?`,
            answer: `After you request a free brake repair quote, you’ll receive an email within 1 hour that will outline our best guess (based on the symptoms you provided) for the repairs and estimated cost. On the day of your appointment, a NuBrakes technician will arrive with all the tools they need to evaluate your brakes in person. Following the in-person evaluation, your technician will provide you with a formal repair quote. Upon your approval, the technician will complete the brake repair procedure.`,
          },
          {
            question: `Do you service all ${cityName} models?`,
            answer: `Yes, we’re able to provide brake service on nearly all ${cityName} models and trims! All of our mobile brake replacement technicians have all of the necessary tools and experience to repair your brakes no matter the model/year. So whether you’re looking for service on a 328i or an M-class, we can take care of your brake service needs.`,
          },
          {
            question: `Do you offer a warranty on the brake repairs?`,
            answer: `Yes, your NuBrakes mobile brake repair is backed by our 24 month, 24,000 mile warranty.`,
          },
        ]}
      />
      <CityNearYouSection
        title={`The Best ${cityName} Mobile Brake Repair Near Me`}
        description={`Stop searching for a local ${cityName} brake shop near you. We cover most of the ${cityName} metro area and bring the brake shop to you.`}
        listOfZips={listOfZips}
        listOfMinorCities={listOfMinorCities}
      />
      <BrakeRepairNearYouWithCities
        title={`We’re the closest mobile brake repair service in ${cityName} near you. Skip waiting in line at the repair shop, we come to you!`}
        cities={cities}
      />
    </Layout>
  )
}
